import ApiManager from './core/api/index';
import { ENVIRONMENT, LANGUAGE, REGION, API_DOMAIN } from './core/constants/index';
import { generateUUID } from './core/utils/index';
import configManager, { GlobalConfig } from './core/config';

const DEFAULT_CONFIG = {
  language: LANGUAGE.ZH_CN,
  region: REGION.DOMESTIC,
  environment: ENVIRONMENT.PRODUCTION
};

function setDefaultConfig (config: GlobalConfig): GlobalConfig {
  return {
    ...DEFAULT_CONFIG,
    ...config
  }
}

/**
 * 初始化 UI 组件库 - 支持 Promise 和 callback 两种调用方式
 * @param config 配置对象
 * @param callback 可选的回调函数
 * @returns Promise<void> 当不传入 callback 时返回 Promise
 */
export function initUIComponents(
  config: GlobalConfig, 
  callback?: (error?: Error | null) => void
): Promise<void> | void {
  const initPromise = async (): Promise<void> => {
    try {
      // 配置默认项
      config = setDefaultConfig(config);

      // 通用请求头配置
      ApiManager.setConfig({
        // @ts-ignore
        baseURL: API_DOMAIN[config.environment][config.region],
        headers: {
          "Accept-Language": config.language as string,
          "X-Lifecycle-Id": generateUUID(),
          "X-Game-Id": String(config.gameId),
          "X-Game-Package": config.packageName as string,
          "X-Device-Os": "h5"
        }
      });

      // ping初始化
      const pingResponse = await ApiManager.getInstance().game.getNetworkAvailable();
      if (pingResponse.code !== 200) {
        return Promise.reject(pingResponse);
      }
      
      // 使用全局配置管理器实例
      await configManager.init(config);
    } catch (error) {
      console.error('UI 组件库初始化失败:', error);
      throw error;
    }
  };

  // 如果传入了 callback，使用 callback 模式
  if (callback) {
    initPromise()
      .then(() => callback(null))
      .catch((error) => callback(error));
    return;
  }

  // 否则返回 Promise
  return initPromise();
}

/**
 * 更新配置
 * @param config 部分配置对象
 */
export function updateConfig(config: GlobalConfig): Promise<void> | void {
  const updataPromise = async (): Promise<void> => {
    try {
      config = { ...configManager.getAll(), ...config };
      ApiManager.getInstance().updataConfig({
        // @ts-ignore
        baseURL: API_DOMAIN[config.environment][config.region],
        headers: {
          "Accept-Language": config.language as string,
          "X-Lifecycle-Id": generateUUID(),
          "X-Game-Id": String(config.gameId),
          "X-Game-Package": config.packageName as string,
          "X-Device-Os": "h5"
        }
      });
      await configManager.update(config);
    } catch(error) {
      console.error('UI 组件库配置更新失败:', error);
      throw error;
    }
  };

  return updataPromise();
}

export type { GlobalConfig }; 